import { bucketLog } from '@bucketplace/bucket-log';
import {
  Flex,
  FONT_STYLE,
  MEDIA_QUERY,
  Text,
  useResponsive,
} from '@bucketplace/design-system';
import { IconPlayFilled } from '@bucketplace/icons';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import {
  ComponentPropsWithoutRef,
  forwardRef,
  PropsWithChildren,
  ReactElement,
} from 'react';

import { ModalButtonWrapper } from '../../ModalButtonWrapper';
import { VideoPreview } from '../../VideoPreview';

/** 다큐 브랜드 필름 컨테이너 */
export function DocumentaryBrandFilmContainer({
  children,
}: PropsWithChildren): ReactElement {
  return (
    <Flex
      direction="column"
      gap={{
        mobile: 16,
        tablet: 16,
        laptop: 28,
        desktop: 28,
      }}
      position="relative"
      w="100%"
      h="100%"
      top={0}
      left={0}
      align="center"
      justify="center"
    >
      {children}
    </Flex>
  );
}

/** 다큐 브랜드 필름 텍스트 */
export const DocumentaryBrandFilmText = forwardRef<
  HTMLParagraphElement,
  ComponentPropsWithoutRef<'p'>
>(({ children }, ref) => {
  return (
    <StyledText
      ref={ref}
      as="p"
      weight={700}
      color="foregroundInverse"
      css={{
        width: 'max-content',
        fontWeight: 700,
        opacity: 0,
      }}
    >
      {children}
    </StyledText>
  );
});

/** 다큐 브랜드 필름 버튼 */
export const DocumentaryBrandFilmButton = forwardRef<
  HTMLButtonElement,
  ComponentPropsWithoutRef<'button'>
>(({ children }, ref) => {
  const { breakpoint } = useResponsive({});
  const isMobile = breakpoint === 'mobile' || breakpoint === 'tablet';

  return (
    <ModalButtonWrapper
      renderModal={({ isOpen, onClose }) =>
        isOpen && (
          <VideoPreview
            type="iframe"
            title="오늘의집 브랜드 필름"
            url="https://www.youtube.com/embed/naiVxIexDA0?si=Hsx4GAdIEtFJDdVR"
            onClose={onClose}
          />
        )
      }
    >
      {({ onClick }) => (
        <StyledButton
          ref={ref}
          type="button"
          aria-haspopup="dialog"
          onClick={() => {
            bucketLog({
              category: 'click',
              type: 'button',
              section_id: 'MINI_DOCUMENTARY',
              id: 'PLAY',
            });

            onClick();
          }}
        >
          <IconPlayFilled
            color="white"
            renderMode="monochrome"
            weight="regular"
            size={isMobile ? 20 : 26}
          />
          {children}
        </StyledButton>
      )}
    </ModalButtonWrapper>
  );
});

const StyledButton = styled.button`
  display: flex;
  flex-direction: row;
  gap: 8px;
  justify-content: center;
  align-items: center;

  width: fit-content;
  color: ${({ theme }) => theme.colors.foregroundInverse};
  font-size: 16px;
  line-height: 24px;
  font-weight: 700;

  background-color: #FFFFFF38;
  backdrop-filter: blur(35px);
  padding: 16px 24px;
  border-radius: 500px;
  opacity: 0;
  pointer-events: none;
  will-change: opacity;

  ${MEDIA_QUERY.min(
    'laptop',
    css`
    padding: 24px 36px 24px 30px;
    ${FONT_STYLE.heading20}
  `,
  )}
`;

const StyledText = styled(Text)`
  font-size: 36px;

  ${MEDIA_QUERY.min(
    'laptop',
    css`
    font-size: 80px;
  `,
  )}
`;
